import { create } from 'zustand';
import { habitsRepo } from '../services/db/habits';
import { checkinsRepo } from '../services/db/checkins';
import { freezesRepo } from '../services/db/freezes';
import { subscriptionApi, habitsApi } from '../services/api';
import { requestSync } from '../services/sync/engine';
import { getToday } from '../utils/dates';

function activeUserIdFromState(state) {
  return state.user?.userId || state.user?.username || state.anonUserId || null;
}

function isAnonymousId(id) {
  return typeof id === 'string' && id.startsWith('anon-');
}

// Re-engagement Channel 2 (shared-docs/re-engagement-nudges.md): the launch-time
// status response may carry an inAppNudge. Stash it for <NudgeBanner> and notify.
const NUDGE_STORAGE_KEY = 'streaks:inAppNudge';
const NUDGE_EVENT = 'streaks-inapp-nudge';
function stashInAppNudge(data) {
  try {
    if (data?.inAppNudge?.id) {
      localStorage.setItem(NUDGE_STORAGE_KEY, JSON.stringify(data.inAppNudge));
    } else {
      localStorage.removeItem(NUDGE_STORAGE_KEY);
    }
    window.dispatchEvent(new CustomEvent(NUDGE_EVENT));
  } catch { /* localStorage unavailable — non-fatal */ }
}

const useStore = create((set, get) => ({
  // Auth state
  user: null,
  anonUserId: null,
  authLoading: true,
  setUser: (user) => set({ user, authLoading: false }),
  setAuthLoading: (loading) => set({ authLoading: loading }),
  setAnonUserId: (id) => set({ anonUserId: id }),

  // Habits state
  habits: [],
  habitsLoading: false,
  todayCheckins: {},
  allCheckins: [],
  allFreezes: [],

  // Sync state — set true once the first server pull for the current
  // authed session has finished (or failed). Used to gate the
  // Dashboard → Onboarding redirect so a freshly-installed but
  // already-signed-in user doesn't get bounced into the wizard
  // before their server-side habits land in Dexie.
  firstSyncComplete: false,
  setFirstSyncComplete: (v) => set({ firstSyncComplete: !!v }),

  fetchHabits: async () => {
    const userId = activeUserIdFromState(get());
    if (!userId) return;
    set({ habitsLoading: true });
    try {
      const habits = await habitsRepo.list(userId);
      const allCheckins = await checkinsRepo.listAllForUser(userId);
      const allFreezes = await freezesRepo.listAllForUser(userId);
      set({ habits, allCheckins, allFreezes, habitsLoading: false });
    } catch (err) {
      console.error('Failed to load local habits:', err);
      set({ habitsLoading: false });
    }
  },

  // Apply a streak freeze for today. Premium, server-verified; on success the
  // freeze is written locally so the freeze-aware streak math updates at once.
  freezeHabit: async (habitId) => {
    const userId = activeUserIdFromState(get());
    if (!userId) throw new Error('No active user');
    const result = await habitsApi.freeze(habitId);
    const day = result?.freeze?.date || getToday();
    await freezesRepo.put(userId, habitId, day);
    const allFreezes = await freezesRepo.listAllForUser(userId);
    set({ allFreezes });
    return result;
  },

  // Called by the sync engine after a push+pull so the UI reflects any
  // server-side changes (other devices, server-side streak math, etc).
  refreshAfterSync: async () => {
    await get().fetchHabits();
  },

  createHabit: async (data) => {
    const userId = activeUserIdFromState(get());
    if (!userId) throw new Error('No active user');
    const habit = await habitsRepo.create(userId, data);
    set((state) => ({ habits: [...state.habits, habit] }));
    requestSync();
    return habit;
  },

  updateHabit: async (habitId, updates) => {
    const userId = activeUserIdFromState(get());
    if (!userId) throw new Error('No active user');
    set((state) => ({
      habits: state.habits.map((h) => (h.habitId === habitId ? { ...h, ...updates } : h)),
    }));
    try {
      await habitsRepo.update(userId, habitId, updates);
      requestSync();
    } catch (err) {
      await get().fetchHabits();
      throw err;
    }
  },

  deleteHabit: async (habitId) => {
    const userId = activeUserIdFromState(get());
    if (!userId) throw new Error('No active user');
    set((state) => ({
      habits: state.habits.filter((h) => h.habitId !== habitId),
      allCheckins: state.allCheckins.filter((c) => c.habitId !== habitId),
    }));
    try {
      await habitsRepo.softDelete(userId, habitId);
      requestSync();
    } catch (err) {
      await get().fetchHabits();
      throw err;
    }
  },

  checkin: async (habitId, date) => {
    const userId = activeUserIdFromState(get());
    if (!userId) throw new Error('No active user');
    // Use the LOCAL calendar day, not UTC. `new Date().toISOString()` rolls over
    // to tomorrow for any user west of UTC in the evening, landing the check-in
    // on the wrong day and breaking their streak. getToday() is local.
    const day = date || getToday();
    const isToday = day === getToday();

    // Only reflect in the "done today" map when the write is actually for today
    // (back-filling a past day must not flip today's button).
    if (isToday) {
      set((state) => ({
        todayCheckins: { ...state.todayCheckins, [habitId]: true },
      }));
    }

    try {
      await checkinsRepo.create(userId, habitId, day);
      const allCheckins = await checkinsRepo.listAllForUser(userId);
      set({ allCheckins });
      requestSync();
      return { date: day };
    } catch (err) {
      if (isToday) {
        set((state) => ({
          todayCheckins: { ...state.todayCheckins, [habitId]: false },
        }));
      }
      throw err;
    }
  },

  undoCheckin: async (habitId, date) => {
    const userId = activeUserIdFromState(get());
    if (!userId) throw new Error('No active user');
    const day = date || getToday();
    const isToday = day === getToday();

    if (isToday) {
      set((state) => ({
        todayCheckins: { ...state.todayCheckins, [habitId]: false },
      }));
    }

    try {
      await checkinsRepo.softDelete(userId, habitId, day);
      const allCheckins = await checkinsRepo.listAllForUser(userId);
      set({ allCheckins });
      requestSync();
    } catch (err) {
      await get().fetchHabits();
      throw err;
    }
  },

  setTodayCheckins: (checkins) => set({ todayCheckins: checkins }),

  // Clear the signed-in user's locally-cached data on sign-out so it isn't
  // visible to the next person on a shared device. Anonymous-scoped data is
  // left intact (the app returns to anonymous mode after sign-out).
  clearSignedInData: async () => {
    const state = get();
    const userId = state.user?.userId || state.user?.username || null;
    if (userId && !isAnonymousId(userId)) {
      try {
        await Promise.all([
          habitsRepo.clearForUser(userId),
          checkinsRepo.clearForUser(userId),
          freezesRepo.clearForUser(userId),
        ]);
      } catch (err) {
        console.error('Failed to clear local data on sign-out:', err);
      }
    }
    set({ habits: [], allCheckins: [], allFreezes: [], todayCheckins: {}, subscription: null });
  },

  // Stats — legacy hook surface, kept for any caller that still imports it.
  overview: null,
  overviewLoading: false,
  fetchOverview: async () => {
    // Stats are computed client-side from `allCheckins` + `habits` via
    // utils/streakCalc.js. This action is a no-op now; left in place so
    // existing call sites don't crash.
  },

  // Subscription
  subscription: null,
  subscriptionLoading: true,

  fetchSubscription: async () => {
    const state = get();
    const userId = activeUserIdFromState(state);
    if (!userId || isAnonymousId(userId)) {
      // Never sell to anonymous users — short-circuit to free.
      set({ subscription: { tier: 'free', premiumPreview: { active: false } }, subscriptionLoading: false });
      return;
    }
    set({ subscriptionLoading: true });
    try {
      const data = await subscriptionApi.status();
      set({ subscription: data, subscriptionLoading: false });
      stashInAppNudge(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      set({ subscriptionLoading: false });
    }
  },

  isPremium: () => {
    const { subscription } = get();
    return subscription?.tier === 'premium' || subscription?.premiumPreview?.active === true;
  },

  // UI state
  showHabitForm: false,
  editingHabit: null,
  showPremiumModal: false,
  setShowHabitForm: (show, habit = null) =>
    set({ showHabitForm: show, editingHabit: habit }),
  setShowPremiumModal: (show) => set({ showPremiumModal: show }),
}));

export default useStore;
